import { useEffect, useState } from 'react'

/**
 * Whether the browser currently has a network interface up.
 *
 * A different axis from `useBreakpoint()`, not a second instance of it — rule
 * 5 pins viewport to one source, and connectivity is not the viewport.
 * `navigator.onLine` plus the `online`/`offline` window events are the one
 * mechanism the platform exposes for this, exactly as `matchMedia` is the one
 * mechanism for breakpoints, so this hook is that mechanism's single point of
 * entry rather than a component reading `navigator.onLine` for itself.
 *
 * **What `true` does not mean.** `navigator.onLine` reports whether the OS
 * reports an active network interface, not whether the internet — or
 * TrackE5's backend specifically — is reachable through it. A machine on a
 * captive portal or a dead Wi-Fi router with no upstream still reads `true`.
 * So this hook produces false NEGATIVES of offline (it can miss real
 * outages) and not false positives (a browser does not report `false` while
 * a connection actually works) — MDN documents the same asymmetry. A caller
 * that needs to know the backend is actually reachable has to ask the
 * backend, not this hook; this hook only ever answers "does the browser
 * think it has a link".
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine)

  useEffect(() => {
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return online
}
